/**
 * Secure PDF Viewer Component
 * Uses react-pdf for view-only PDF rendering
 * Prevents direct download and right-click context menu
 */
import React, { useState, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Loader2, AlertCircle } from 'lucide-react';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const SecurePDFViewer = ({ 
  pdfBlob, 
  className = '',
  height = '600px',
  showPageControls = true,
  showZoomControls = true,
}) => {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Handle document load success
  const onDocumentLoadSuccess = useCallback(({ numPages }) => {
    setNumPages(numPages);
    setPageNumber(1);
    setIsLoading(false);
    setError(null);
  }, []);

  // Handle document load error
  const onDocumentLoadError = useCallback((err) => {
    console.error('PDF load error:', err);
    setError('Failed to load PDF document');
    setIsLoading(false);
  }, []);

  // Page navigation handlers
  const goToPreviousPage = () => {
    setPageNumber((prev) => Math.max(prev - 1, 1));
  };

  const goToNextPage = () => {
    setPageNumber((prev) => Math.min(prev + 1, numPages || 1));
  };

  // Zoom handlers
  const zoomIn = () => {
    setScale((prev) => Math.min(prev + 0.2, 2.5));
  };

  const zoomOut = () => {
    setScale((prev) => Math.max(prev - 0.2, 0.5));
  };

  // Prevent context menu (right-click)
  const handleContextMenu = (e) => {
    e.preventDefault();
    return false;
  };

  // Prevent keyboard shortcuts for saving/printing
  const handleKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'p')) {
      e.preventDefault();
      return false;
    }
  };

  if (!pdfBlob) {
    return (
      <div 
        className={`flex items-center justify-center bg-gray-100 rounded-lg ${className}`}
        style={{ height }}
      >
        <div className="text-center text-gray-500">
          <AlertCircle className="mx-auto mb-2" size={40} />
          <p>No PDF document provided</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`bg-white rounded-lg shadow-lg overflow-hidden ${className}`}
      onContextMenu={handleContextMenu}
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      {/* Controls Header */}
      <div className="bg-gray-100 border-b px-4 py-3 flex items-center justify-between">
        {/* Page Controls */}
        {showPageControls && (
          <div className="flex items-center gap-3">
            <button
              onClick={goToPreviousPage}
              disabled={pageNumber <= 1 || isLoading}
              className="p-2 bg-white border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              aria-label="Previous page"
            >
              <ChevronLeft size={20} />
            </button>
            <span className="text-gray-700 font-medium min-w-[100px] text-center">
              {isLoading ? 'Loading...' : `Page ${pageNumber} of ${numPages || '?'}`}
            </span>
            <button
              onClick={goToNextPage}
              disabled={pageNumber >= (numPages || 1) || isLoading}
              className="p-2 bg-white border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              aria-label="Next page"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        )}

        {/* Zoom Controls */}
        {showZoomControls && (
          <div className="flex items-center gap-2">
            <button
              onClick={zoomOut}
              disabled={scale <= 0.5 || isLoading}
              className="p-2 bg-white border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              aria-label="Zoom out"
            >
              <ZoomOut size={20} />
            </button>
            <span className="text-gray-600 text-sm font-medium min-w-[50px] text-center">
              {Math.round(scale * 100)}%
            </span>
            <button
              onClick={zoomIn}
              disabled={scale >= 2.5 || isLoading}
              className="p-2 bg-white border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              aria-label="Zoom in"
            >
              <ZoomIn size={20} />
            </button>
          </div>
        )}
      </div>

      {/* PDF Document */}
      <div 
        className="overflow-auto bg-gray-50 select-none"
        style={{ height, userSelect: 'none' }}
      >
        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <Loader2 className="animate-spin mx-auto mb-2 text-blue-600" size={40} />
              <p className="text-gray-600">Loading PDF...</p>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="flex items-center justify-center h-full">
            <div className="text-center text-red-500">
              <AlertCircle className="mx-auto mb-2" size={40} />
              <p>{error}</p>
            </div>
          </div>
        )}

        {/* PDF Document */}
        <Document
          file={pdfBlob}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          loading={null}
          className="flex justify-center py-4"
        >
          <Page
            pageNumber={pageNumber}
            scale={scale}
            loading={null}
            renderTextLayer={false}
            renderAnnotationLayer={false}
            className="shadow-lg"
          />
        </Document>
      </div>

      {/* Security overlay to prevent screenshots (visual indicator) */}
      <style>{`
        .react-pdf__Document {
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
          user-select: none;
        }
        .react-pdf__Page canvas {
          -webkit-user-drag: none;
          -khtml-user-drag: none;
          -moz-user-drag: none;
          -o-user-drag: none;
          user-drag: none;
        }
      `}</style>
    </div>
  );
};

export default SecurePDFViewer;
